import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Popover,
  TextField,
  Stack,
  Badge,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import UnfoldMoreDoubleRounded from "@mui/icons-material/UnfoldMoreDoubleRounded";
import FilterListRounded from "@mui/icons-material/FilterListRounded";
import DraggableCard from "./DraggableCard";
import { formatBRL } from "../../utils/utils";
import { useTranslation } from "react-i18next";

const PipelineStage = ({
  stage,
  cards,
  onStageDragOver,
  onStageDrop,
  onCardDragStart,
  // onDragOver,
  // onDrop,
  // onDragStart,
  draggingOverStage,
  draggingOverIndex,
  draggedCard,
  totalCards,
  compact = true,
  onOpenDetails,
  onStageClick,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  softRefresh,
  stageTotalCount,
  stageSearchQuery = "",
  onApplyStageSearch,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const [collapsed, setCollapsed] = useState(false);
  const [visible, setVisible] = useState(8);
  const listRef = useRef(null);
  const loadMoreThrottleRef = useRef(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterDraft, setFilterDraft] = useState(stageSearchQuery || "");

  useEffect(() => {
    setVisible(8);
    loadMoreThrottleRef.current = 0;
  }, [stage?.id]);

  useEffect(() => {
    setFilterDraft(stageSearchQuery || "");
  }, [stageSearchQuery, stage?.id]);

  const pct = useMemo(() => {
    const t = totalCards || 1;
    return Math.min(100, Math.round(((cards?.length || 0) / t) * 100));
  }, [cards, totalCards]);

  const isCardInCurrentStage =
    draggedCard && cards?.some((c) => c.id === draggedCard.card.id);

  const showPlaceholder =
    draggingOverStage === stage.id && draggingOverIndex === null && !isCardInCurrentStage;

  const visibleCards = (cards || []).slice(0, collapsed ? 0 : visible);
  const restLocal = Math.max(0, (cards?.length || 0) - visibleCards.length);

  const stats = useMemo(() => {
    const totalValue = (cards || []).reduce((s, c) => s + (Number(c.total) || 0), 0);
    const itemsCount = (cards || []).reduce((s, c) => s + (c.items?.length || 0), 0);
    const avgTicket = (cards?.length ? totalValue / cards.length : 0);
    return { totalValue, itemsCount, avgTicket };
  }, [cards]);

  // rolagem infinita dentro da coluna (throttle evita disparar várias páginas ao mesmo tempo em colunas pesadas)
  const handleScroll = (e) => {
    const el = e.currentTarget;
    const threshold = 64;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      if (!hasMore || loadingMore || !onLoadMore) return;
      const now = Date.now();
      if (now - loadMoreThrottleRef.current < 450) return;
      loadMoreThrottleRef.current = now;
      onLoadMore(stage.id);
    }
  };

  const unsetHighlight = !!stage?.isUnsetSubtipo1;

  const headerCount =
    stageTotalCount != null && Number.isFinite(Number(stageTotalCount))
      ? Number(stageTotalCount)
      : cards?.length || 0;

  const showStageFilter =
    typeof onApplyStageSearch === "function" &&
    (headerCount > 10 || Boolean(stageSearchQuery?.trim()));

  const filterOpen = Boolean(filterAnchorEl);

  const handleOpenFilter = (e) => {
    setFilterDraft(stageSearchQuery || "");
    setFilterAnchorEl(e.currentTarget);
  };

  const handleCloseFilter = () => setFilterAnchorEl(null);

  const handleApplyFilter = async () => {
    await onApplyStageSearch(filterDraft);
    handleCloseFilter();
  };

  const handleClearFilter = async () => {
    setFilterDraft("");
    await onApplyStageSearch("");
    handleCloseFilter();
  };

  return (
    <Paper
      sx={{
        backgroundColor: unsetHighlight
          ? alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.14 : 0.2)
          : theme.palette.background.default,
        borderRadius: 2,
        boxShadow: "0px 4px 6px rgba(0,0,0,0.08)",
        padding: compact ? 1.25 : 2,
        minWidth: compact ? 300 : 360,
        maxWidth: compact ? 340 : 420,
        display: "flex",
        flexDirection: "column",
        border: unsetHighlight
          ? `2px solid ${alpha(theme.palette.warning.dark, theme.palette.mode === "dark" ? 0.85 : 0.55)}`
          : `1px solid ${theme.palette.divider}`,
      }}
      onDragOver={(e) => onStageDragOver(e, stage.id, null)}
      onDrop={(e) => onStageDrop(e, stage.id)}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography
          variant={compact ? "subtitle1" : "h6"}
          sx={{
            fontWeight: 700,
            color: unsetHighlight ? theme.palette.warning.dark : theme.palette.primary.main,
            mr: 1,
            cursor: ((stage?.children || []).length > 0) ? "pointer" : "default",
            textDecoration: ((stage?.children || []).length > 0) ? "underline" : "none",
            userSelect: "none",
          }}
          onClick={onStageClick}
          title={((stage?.children || []).length > 0) ? "Ver subetapas" : undefined}
        >
          {stage.name} ({headerCount})
        </Typography>
        <Box sx={{ flex: 1 }} />
        {showStageFilter && (
          <>
            <Tooltip title={t("salesPipelinePage.stageFilterTooltip")}>
              <IconButton
                size="small"
                aria-label={t("salesPipelinePage.stageFilterTooltip")}
                onClick={handleOpenFilter}
                color={stageSearchQuery ? "primary" : "default"}
              >
                <Badge color="primary" variant="dot" invisible={!stageSearchQuery}>
                  <FilterListRounded fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Popover
              open={filterOpen}
              anchorEl={filterAnchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: { p: 2, minWidth: 280, maxWidth: 360 },
                },
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {t("salesPipelinePage.stageFilterTitle")}
              </Typography>
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder={t("salesPipelinePage.stageFilterPlaceholder")}
                value={filterDraft}
                onChange={(e) => setFilterDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleApplyFilter();
                }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button size="small" onClick={handleClearFilter} disabled={loadingMore}>
                  {t("salesPipelinePage.stageFilterClear")}
                </Button>
                <Button size="small" variant="contained" onClick={() => void handleApplyFilter()} disabled={loadingMore}>
                  {t("salesPipelinePage.stageFilterApply")}
                </Button>
              </Stack>
            </Popover>
          </>
        )}
        <Tooltip title={collapsed ? "Expandir" : "Recolher"}>
          <IconButton size="small" onClick={() => setCollapsed((v) => !v)}>
            {collapsed ? <ExpandMoreRounded /> : <ExpandLessRounded />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Descrição */}
      {!compact && stage.description && !collapsed && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {stage.description}
        </Typography>
      )}

      {/* Progresso + KPIs do estágio */}
      {!collapsed && (
        <>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: compact ? 4 : 6,
              borderRadius: 3,
              mb: 1,
              backgroundColor: theme.palette.action.hover,
              "& .MuiLinearProgress-bar": {
                backgroundColor: unsetHighlight ? theme.palette.warning.main : theme.palette.primary.main,
              },
            }}
          />
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
            <Chip size="small" variant="outlined" label={`Valor: ${formatBRL(stats.totalValue)}`} />
            <Chip size="small" variant="outlined" label={`Ticket médio: ${formatBRL(stats.avgTicket)}`} />
            <Chip size="small" variant="outlined" label={`${stats.itemsCount} item${stats.itemsCount === 1 ? "" : "s"}`} />
          </Box>
        </>
      )}

      <Divider />

      {/* Cards */}
      <Box
        ref={listRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minHeight: 80,
          mt: 2,
          maxHeight: "calc(100vh - 260px)", // rolagem vertical por coluna
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        {!collapsed && showPlaceholder && (
          <Box
            sx={{
              height: 56,
              border: (t) => `2px dashed ${t.palette.primary.main}`,
              borderRadius: 1,
              mb: 1,
            }}
          />
        )}

        {!collapsed &&
          visibleCards.map((card, index) => (
            <React.Fragment key={card.id}>
              {draggingOverStage === stage.id &&
                draggingOverIndex === index &&
                !isCardInCurrentStage && (
                  <Box
                    sx={{
                      height: 56,
                      border: (t) => `2px dashed ${t.palette.primary.main}`,
                      borderRadius: 1,
                      mb: 1,
                    }}
                  />
                )}

              <DraggableCard
                card={card}
                stageId={stage.id}
                // onDragStart={onDragStart}
                // onDragOver={onDragOver}
                onDragStart={onCardDragStart}
                onDragOver={onStageDragOver}
                isDragged={draggedCard?.card.id === card.id}
                compact={compact}
                onOpenDetails={onOpenDetails}
                onChangeStatus={softRefresh}
              />
            </React.Fragment>
          ))}

        {!collapsed && (cards?.length || 0) === 0 && !loadingMore && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.disabled,
              textAlign: "center",
              fontStyle: "italic",
              mt: 2,
            }}
          >
            Nenhum pedido neste estágio.
          </Typography>
        )}

        {!collapsed && loadingMore && (cards?.length || 0) === 0 && (
          <>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Skeleton variant="rounded" height={compact ? 72 : 90} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </>
        )}

        {!collapsed && loadingMore && (cards?.length || 0) > 0 && (
          <Box sx={{ mt: 1 }}>
            <Skeleton variant="rounded" height={compact ? 72 : 90} sx={{ borderRadius: 1 }} />
          </Box>
        )}

        {/* Mostrar mais / Carregar mais */}
        {!collapsed && (hasMore || restLocal > 0) && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
            <Button
              size="small"
              variant="text"
              startIcon={loadingMore ? <CircularProgress size={16} /> : <UnfoldMoreDoubleRounded />}
              onClick={() => {
                if (restLocal > 0) setVisible((v) => v + 8);
                else if (hasMore && onLoadMore) onLoadMore(stage.id);
              }}
              disabled={loadingMore}
            >
              {loadingMore ? "Carregando…" : restLocal > 0 ? `Mostrar mais (+${restLocal})` : "Carregar mais"}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PipelineStage;
